import React, { lazy, Suspense, useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { BareThemeProvider } from './components/design-system/theme';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { authAPI } from './services/api';
import { logger } from './utils/logger';

// Contexts
import { OrgProvider } from './contexts/OrgContext';
import { PerspectiveProvider } from './contexts/PerspectiveContext';

// Always-rendered components (keep static imports)
import InternalNavigation from './components/InternalNavigation';
import ClientNavigation from './components/ClientNavigation';
import RepNavigation from './components/RepNavigation';
import ProtectedRoute from './components/ProtectedRoute';
import FeatureGatedRoute from './components/FeatureGatedRoute';
import PerspectiveIndicator from './components/PerspectiveIndicator';
import ClientRouteWrapper from './components/ClientRouteWrapper';
import RepRouteWrapper from './components/RepRouteWrapper';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import PlaceOrderRouter from './components/PlaceOrderRouter';

// Lazy-loaded page components
const SplashPage = lazy(() => import('./components/splash/LaunchReadySplashPage'));
const ShopLanding = lazy(() => import('./pages/ShopLanding'));
const FigmaProductDetail = lazy(() => import('./pages/FigmaProductDetail'));
const PasswordlessLogin = lazy(() => import('./pages/auth/PasswordlessLogin'));
const Signup = lazy(() => import('./pages/auth/Signup'));
const PendingApproval = lazy(() => import('./pages/auth/PendingApproval'));
const OrgSelect = lazy(() => import('./pages/OrgSelect'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const PromoCodeManagement = lazy(() => import('./pages/admin/PromoCodeManagement'));
const RepManagement = lazy(() => import('./pages/admin/RepManagement'));
const InvoiceManagement = lazy(() => import('./pages/admin/InvoiceManagement'));
const InvoiceDetailPage = lazy(() => import('./pages/admin/InvoiceDetailPage'));
const Accounting = lazy(() => import('./pages/admin/Accounting'));
const ProductContentEditor = lazy(() => import('./components/admin/ProductContentEditor'));
const EPLCalendar = lazy(() => import('./pages/admin/EPLCalendar'));
const ResourceManagement = lazy(() => import('./pages/internal/ResourceManagement'));
const AuditLogs = lazy(() => import('./pages/internal/AuditLogs'));

// Internal Pages
const BareDashboard = lazy(() => import('./pages/internal/BareDashboard'));
const EPLDashboard = lazy(() => import('./pages/internal/EPLDashboard'));
const PurchaseOrders = lazy(() => import('./pages/internal/PurchaseOrders'));
const ReceiveInventoryWorkflow = lazy(() => import('./pages/internal/ReceiveInventoryWorkflow'));
const ReceiveInventoryBatchDetail = lazy(() => import('./pages/internal/ReceiveInventoryBatchDetail'));
const RawMaterials = lazy(() => import('./pages/internal/RawMaterials'));
const ClientManagement = lazy(() => import('./pages/internal/ClientManagement'));
const AdminClientDetailPage = lazy(() => import('./pages/internal/AdminClientDetailPage'));
const ClientAnalytics = lazy(() => import('./pages/internal/ClientAnalytics'));
const OrderProcessing = lazy(() => import('./pages/internal/OrderProcessing'));
const InventoryTracker = lazy(() => import('./pages/internal/InventoryTracker'));
const GlobalInventoryAudit = lazy(() => import('./pages/internal/GlobalInventoryAudit'));
const InternalSettings = lazy(() => import('./pages/internal/Settings'));
const InternalUserSettings = lazy(() => import('./pages/internal/UserSettings'));
const DonorManagement = lazy(() => import('./pages/internal/DonorManagement'));
const DonorDetailPage = lazy(() => import('./pages/internal/DonorDetailPage'));
const CrmDashboard = lazy(() => import('./pages/internal/CrmDashboard'));
const CompanyDetail = lazy(() => import('./pages/internal/CompanyDetail'));
const CompanyManagement = lazy(() => import('./pages/internal/CompanyManagement'));
const AdvancedAnalytics = lazy(() => import('./pages/internal/AdvancedAnalytics'));
const TransferOrders = lazy(() => import('./pages/internal/TransferOrders'));
const InternalHorsesPage = lazy(() => import('./pages/internal/HorsesPage'));
const PackingListDocument = lazy(() => import('./pages/internal/PackingListDocument'));

// Client Pages
const ClientTransferHistory = lazy(() => import('./pages/client/TransferHistory'));
const ClientDashboard = lazy(() => import('./pages/client/Dashboard'));
const PlaceOrderNew = lazy(() => import('./pages/client/PlaceOrderNew'));
const PlaceOrderLipoderma = lazy(() => import('./pages/client/PlaceOrderLipoderma'));
const Checkout = lazy(() => import('./pages/client/Checkout'));
const OrderHistory = lazy(() => import('./pages/client/OrderHistory'));
const InvoicePaymentPage = lazy(() => import('./pages/client/InvoicePaymentPage'));
const ClientInventoryManagement = lazy(() => import('./pages/client/InventoryManagement'));
const ShippingLocations = lazy(() => import('./pages/client/ShippingLocations'));
const ClientSettings = lazy(() => import('./pages/client/Settings'));
const Payments = lazy(() => import('./pages/client/Payments'));
const MyPayments = lazy(() => import('./pages/client/MyPayments'));
const PayInvoice = lazy(() => import('./pages/client/PayInvoice'));
const PayInvoicePartial = lazy(() => import('./pages/client/PayInvoicePartial'));
const MyShippingAddress = lazy(() => import('./pages/client/MyShippingAddress'));
const ClientPricing = lazy(() => import('./pages/client/ClientPricing'));
const Confirmation = lazy(() => import('./pages/client/Confirmation'));
const PaymentSuccess = lazy(() => import('./pages/client/PaymentSuccess'));
const Resources = lazy(() => import('./pages/client/Resources'));
const ClientHorsesPage = lazy(() => import('./pages/client/HorsesPage'));
const ClientHorseDetailPage = lazy(() => import('./pages/client/HorseDetailPage'));

// Rep Pages
const RepDashboard = lazy(() => import('./pages/rep/RepDashboard'));
const MyClientsPage = lazy(() => import('./pages/rep/MyClientsPage'));
const RepClientManagement = lazy(() => import('./pages/rep/RepClientManagement'));
const CRMLeadsPage = lazy(() => import('./pages/rep/CRMLeadsPage'));
const LeadDetailPage = lazy(() => import('./pages/rep/LeadDetailPage'));
const OrdersPage = lazy(() => import('./pages/rep/OrdersPage'));
const PromoCodesPage = lazy(() => import('./pages/rep/PromoCodesPage'));
const AnalyticsPage = lazy(() => import('./pages/rep/AnalyticsPage'));
const ResourcesPage = lazy(() => import('./pages/rep/ResourcesPage'));
const InventoryPage = lazy(() => import('./pages/rep/InventoryPage'));
const RepTransfersPage = lazy(() => import('./pages/rep/TransfersPage'));
const RepClientDetailPage = lazy(() => import('./pages/rep/RepClientDetailPage'));
const RepHorsesPage = lazy(() => import('./pages/rep/HorsesPage'));

// Public Pages
const MdReviewPage = lazy(() => import('./pages/public/MdReviewPage'));

const PageLoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <CircularProgress />
  </Box>
);

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);
  const navigate = useNavigate();

  // Load user from sessionStorage on mount (persist on refresh, clear on browser close)
  // Also try localStorage as fallback for new tabs opened via window.open (sessionStorage isn't shared)
  useEffect(() => {
    let savedUser = sessionStorage.getItem('barebiologics_currentUser');

    // Fallback to localStorage for new tabs (e.g., packing list opened in new window)
    if (!savedUser) {
      const localStorageUser = localStorage.getItem('currentUser');
      if (localStorageUser) {
        savedUser = localStorageUser;
        // Copy to sessionStorage for this tab
        sessionStorage.setItem('barebiologics_currentUser', localStorageUser);
        logger.log('User restored from localStorage (new tab)');
      }
    }

    if (savedUser) {
      try {
        const user = JSON.parse(savedUser);
        setCurrentUser(user);

        // Check if authentication tokens exist
        const authToken = localStorage.getItem('authToken');
        const refreshToken = localStorage.getItem('refreshToken');

        if (!authToken || !refreshToken) {
          logger.log('User found but tokens missing, clearing user session');
          sessionStorage.removeItem('barebiologics_currentUser');
          sessionStorage.removeItem('barebiologics_perspective');
          setCurrentUser(null);
        } else {
          logger.log('User and tokens restored successfully');
        }
      } catch (e) {
        logger.error('Error parsing saved user:', e);
        sessionStorage.removeItem('barebiologics_currentUser');
      }
    }
    setIsSessionLoading(false);
  }, []);

  // Inactivity auto-logout + sliding session refresh
  useEffect(() => {
    if (!currentUser) return;

    const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
    let lastActivity = Date.now();
    const updateActivity = () => { lastActivity = Date.now(); };
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    activityEvents.forEach(evt => window.addEventListener(evt, updateActivity));

    const checkInterval = setInterval(async () => {
      try {
        const accessToken = localStorage.getItem('authToken');
        const refreshTokenVal = localStorage.getItem('refreshToken');
        if (!accessToken || !refreshTokenVal) return;

        const idleMs = Date.now() - lastActivity;

        // Auto-logout after 15 minutes of inactivity
        if (idleMs >= INACTIVITY_TIMEOUT_MS) {
          logger.log(`Auto-logout: user inactive for ${Math.round(idleMs / 60000)} minutes`);
          // Silently revoke refresh token on the server (best-effort)
          authAPI.logout().catch(() => {});
          handleLogout();
          return;
        }

        // Sliding session refresh: refresh token before expiry if user is active
        const parts = accessToken.split('.');
        if (parts.length !== 3) return;
        let payload;
        try {
          payload = JSON.parse(atob(parts[1]));
        } catch {
          return;
        }
        const exp = payload?.exp;
        if (!exp) return;

        const nowSec = Date.now() / 1000;
        const timeLeftSec = exp - nowSec;

        // Refresh if <60s left and user was active within last 5 minutes
        if (timeLeftSec <= 60 && idleMs < 5 * 60 * 1000) {
          const res = await authAPI.refreshToken();
          const newAccessToken = res?.accessToken || res?.token;
          if (newAccessToken) {
            localStorage.setItem('authToken', newAccessToken);
          }
        }
      } catch {
        // Silent fail; next cycle will retry
      }
    }, 30000); // Check every 30 seconds

    return () => {
      clearInterval(checkInterval);
      activityEvents.forEach(evt => window.removeEventListener(evt, updateActivity));
    };
  }, [currentUser]);

  const handleLogin = (user) => {
    setCurrentUser(user);
    sessionStorage.setItem('barebiologics_currentUser', JSON.stringify(user));
    localStorage.setItem('currentUser', JSON.stringify(user));
    logger.log('User logged in and stored in both sessionStorage and localStorage');
  };

  const handleLogout = () => {
    setCurrentUser(null);
    sessionStorage.removeItem('barebiologics_currentUser');
    sessionStorage.removeItem('barebiologics_perspective');
    localStorage.removeItem('barebiologics_org');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('currentUser');
    logger.log('User logged out - all tokens and perspective cleared');
    navigate('/login');
  };

  const handleOrderSubmit = (order) => {
    logger.log('Order submitted:', order);
  };

  return (
    <OrgProvider>
      <PerspectiveProvider>
        <BareThemeProvider>
          <CssBaseline />
          <PerspectiveIndicator />

          <Suspense fallback={<PageLoadingFallback />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={!currentUser ? <SplashPage /> : <Navigate to={currentUser.userType === 'internal' ? '/org-select' : `/${currentUser.userType}/place-order`} replace />} />
              <Route path="/login" element={!currentUser ? <PasswordlessLogin onLogin={handleLogin} /> : <Navigate to={currentUser.userType === 'internal' ? '/org-select' : `/${currentUser.userType}/place-order`} replace />} />
              <Route path="/signup" element={!currentUser ? <Signup /> : <Navigate to={currentUser.userType === 'internal' ? '/org-select' : `/${currentUser.userType}/place-order`} replace />} />
              <Route path="/signup/pending" element={<PendingApproval />} />
              <Route
                path="/admin"
                element={
                  isSessionLoading
                    ? <PageLoadingFallback />
                    : currentUser?.isSuperAdmin
                      ? <AdminDashboard currentUser={currentUser} />
                      : <Navigate to="/" replace />
                }
              />
              <Route path="/md-review/:token" element={<MdReviewPage />} />
              <Route path="/products" element={<ShopLanding />} />
              <Route path="/insights" element={<SplashPage />} />

              {/* Org selection for internal users */}
              <Route
                path="/org-select"
                element={
                  <ProtectedRoute requiredUserType="internal" currentUser={currentUser} isLoading={isSessionLoading}>
                    {(() => {
                      if (isSessionLoading) {
                        return <PageLoadingFallback />;
                      }

                      const isSuperAdmin = currentUser?.isSuperAdmin === true;
                      const orgRaw = currentUser?.organization;
                      const normalizedOrg = typeof orgRaw === 'string' ? orgRaw.toLowerCase() : undefined;
                      const isBoth = typeof orgRaw === 'string' && orgRaw.toUpperCase() === 'BOTH';

                      if (isSuperAdmin || isBoth || !normalizedOrg) {
                        return <OrgSelect currentUser={currentUser} />;
                      }

                      if (normalizedOrg === 'bare' || normalizedOrg === 'epl') {
                        return <Navigate to={`/internal/${normalizedOrg}/dashboard`} replace />;
                      }

                      return <OrgSelect currentUser={currentUser} />;
                    })()}
                  </ProtectedRoute>
                }
              />

              {/* Standalone print routes */}
              <Route
                path="/print/packing-list/:orderId"
                element={<PackingListDocument />}
              />
              <Route
                path="/preview/invoice/:orderId"
                element={<InvoicePaymentPage currentUser={currentUser} />}
              />

              {/* Internal routes with org requirement */}
              <Route
                path="/internal/*"
                element={
                  <ProtectedRoute
                    requiredUserType="internal"
                    requireOrg={true}
                    currentUser={currentUser}
                  >
                    <RouteErrorBoundary>
                      <InternalNavigation currentUser={currentUser} onLogout={handleLogout}>
                        <Routes>
                          {/* Bare Biologics routes */}
                          <Route path="bare/dashboard" element={<BareDashboard />} />
                          <Route path="bare/purchase-orders" element={<PurchaseOrders currentCompany="bare" />} />
                          <Route path="bare/receive-inventory" element={<ReceiveInventoryWorkflow currentCompany="bare" />} />
                          <Route path="bare/receive-inventory/batches/:batchId" element={<ReceiveInventoryBatchDetail currentCompany="bare" />} />
                          <Route path="bare/raw-materials" element={<RawMaterials />} />
                          <Route path="bare/order-processing" element={<OrderProcessing />} />
                          <Route path="bare/packing-list/:orderId" element={<PackingListDocument />} />
                          <Route path="bare/inventory-tracker" element={<InventoryTracker />} />
                          <Route path="bare/client-management" element={<ClientManagement />} />
                          <Route path="bare/clients/:id" element={<AdminClientDetailPage currentUser={currentUser} />} />
                          <Route path="bare/leads/:id" element={<LeadDetailPage currentUser={currentUser} />} />
                          <Route path="bare/client-analytics" element={<ClientAnalytics />} />
                          <Route path="bare/companies" element={<CompanyManagement />} />
                          <Route path="bare/analytics" element={<AdvancedAnalytics />} />
                          <Route path="bare/crm" element={<CrmDashboard />} />
                          <Route path="bare/companies/:companyId/*" element={<CompanyDetail />} />
                          <Route path="bare/settings" element={<InternalSettings />} />
                          <Route path="bare/user-settings" element={<InternalUserSettings />} />
                          <Route path="bare/admin/global-inventory-audit" element={<GlobalInventoryAudit />} />
                          <Route path="bare/admin/promo-codes" element={<PromoCodeManagement />} />
                          <Route path="bare/admin/rep-management" element={<RepManagement />} />
                          <Route path="bare/admin/resources" element={<ResourceManagement />} />
                          <Route path="bare/admin/audit-logs" element={<AuditLogs />} />
                          <Route path="bare/horses" element={<InternalHorsesPage currentUser={currentUser} />} />
                          <Route path="bare/horses/:horseId" element={<ClientHorseDetailPage currentUser={currentUser} basePath="/internal/bare" />} />
                          <Route path="bare/admin/invoice-management" element={<InvoiceManagement currentUser={currentUser} />} />
                          <Route path="bare/admin/invoice/:orderId" element={<InvoiceDetailPage currentUser={currentUser} />} />

                          {/* EPL routes */}
                          <Route path="epl/dashboard" element={<EPLDashboard />} />
                          <Route path="epl/donor-management" element={<DonorManagement currentCompany="epl" />} />
                          <Route path="epl/donor-management/:donorId" element={<DonorDetailPage currentCompany="epl" />} />
                          <Route path="epl/purchase-orders" element={<PurchaseOrders currentCompany="epl" />} />
                          <Route path="epl/receive-inventory" element={<ReceiveInventoryWorkflow currentCompany="epl" />} />
                          <Route path="epl/receive-inventory/batches/:batchId" element={<ReceiveInventoryBatchDetail currentCompany="epl" />} />
                          <Route path="epl/raw-materials" element={<RawMaterials />} />
                          <Route path="epl/order-processing" element={<OrderProcessing />} />
                          <Route path="epl/packing-list/:orderId" element={<PackingListDocument />} />
                          <Route path="epl/transfer-orders" element={<TransferOrders />} />
                          <Route path="epl/inventory-tracker" element={<InventoryTracker />} />
                          <Route path="epl/client-management" element={<ClientManagement />} />
                          <Route path="epl/clients/:id" element={<AdminClientDetailPage currentUser={currentUser} />} />
                          <Route path="epl/leads/:id" element={<LeadDetailPage currentUser={currentUser} />} />
                          <Route path="epl/client-analytics" element={<ClientAnalytics />} />
                          <Route path="epl/companies" element={<CompanyManagement />} />
                          <Route path="epl/analytics" element={<AdvancedAnalytics />} />
                          <Route path="epl/crm" element={<CrmDashboard />} />
                          <Route path="epl/companies/:companyId/*" element={<CompanyDetail />} />
                          <Route path="epl/settings" element={<InternalSettings />} />
                          <Route path="epl/user-settings" element={<InternalUserSettings />} />
                          <Route path="epl/admin/global-inventory-audit" element={<GlobalInventoryAudit />} />
                          <Route path="epl/admin/promo-codes" element={<PromoCodeManagement />} />
                          <Route path="epl/admin/rep-management" element={<RepManagement />} />
                          <Route path="epl/admin/resources" element={<ResourceManagement />} />
                          <Route path="epl/admin/audit-logs" element={<AuditLogs />} />
                          <Route path="epl/horses" element={<InternalHorsesPage currentUser={currentUser} />} />
                          <Route path="epl/horses/:horseId" element={<ClientHorseDetailPage currentUser={currentUser} basePath="/internal/epl" />} />
                          <Route path="epl/admin/accounting" element={<Accounting currentUser={currentUser} />} />
                          <Route path="epl/admin/invoice-management" element={<Navigate to="/internal/epl/admin/accounting" replace />} />
                          <Route path="epl/admin/invoice/:orderId" element={<InvoiceDetailPage currentUser={currentUser} />} />
                          <Route path="epl/admin/calendar" element={<EPLCalendar />} />
                          <Route path="epl/admin/product-editor/:productId" element={<ProductContentEditor />} />

                          <Route path="*" element={<Navigate to="/org-select" replace />} />
                        </Routes>
                      </InternalNavigation>
                    </RouteErrorBoundary>
                  </ProtectedRoute>
                }
              />

              {/* Client routes - also accessible by admins in switched client view */}
              <Route
                path="/client/*"
                element={
                  <ClientRouteWrapper currentUser={currentUser} isLoading={isSessionLoading}>
                    <RouteErrorBoundary>
                      <ClientNavigation currentUser={currentUser} onLogout={handleLogout}>
                        <Routes>
                          <Route path="dashboard" element={<ClientDashboard currentUser={currentUser} />} />
                          <Route path="pricing/:product" element={<ClientPricing />} />
                          <Route
                            path="place-order"
                            element={
                              <PlaceOrderRouter currentUser={currentUser} onOrderSubmit={handleOrderSubmit} />
                            }
                          />
                          <Route path="checkout" element={<Checkout currentUser={currentUser} />} />
                          <Route path="orders" element={<OrderHistory currentUser={currentUser} />} />
                          <Route path="invoice-payment/:orderId" element={<InvoicePaymentPage currentUser={currentUser} />} />
                          <Route path="transfers" element={<ClientTransferHistory />} />
                          <Route path="inventory-management" element={<ClientInventoryManagement currentUser={currentUser} />} />
                          <Route path="horses" element={<ClientHorsesPage currentUser={currentUser} />} />
                          <Route path="horses/:horseId" element={<ClientHorseDetailPage currentUser={currentUser} />} />
                          <Route path="payments" element={<Payments />} />
                          <Route path="my-payments" element={<MyPayments currentUser={currentUser} />} />
                          <Route path="pay-invoice" element={<PayInvoicePartial currentUser={currentUser} />} />
                          <Route path="my-shipping-address" element={<MyShippingAddress currentUser={currentUser} />} />
                          <Route path="shipping-locations" element={<ShippingLocations currentUser={currentUser} />} />
                          <Route path="resources" element={<Resources />} />
                          <Route path="settings" element={<ClientSettings currentUser={currentUser} />} />
                          <Route path="confirmation" element={<Confirmation />} />
                          <Route path="payment-success" element={<PaymentSuccess />} />
                          <Route path="product/:productType" element={<FigmaProductDetail />} />
                          <Route path="*" element={<Navigate to="/client/place-order" replace />} />
                        </Routes>
                      </ClientNavigation>
                    </RouteErrorBoundary>
                  </ClientRouteWrapper>
                }
              />

              {/* Rep routes - also accessible by admins in switched rep view */}
              <Route
                path="/rep/*"
                element={
                  <RepRouteWrapper currentUser={currentUser} isLoading={isSessionLoading}>
                    <RouteErrorBoundary>
                      <RepNavigation currentUser={currentUser} onLogout={handleLogout}>
                        <Routes>
                          <Route path="dashboard" element={<RepDashboard currentUser={currentUser} />} />
                          <Route path="clients" element={<RepClientManagement currentUser={currentUser} />} />
                          <Route path="clients/:id" element={<RepClientDetailPage currentUser={currentUser} />} />
                          <Route path="crm-leads" element={<CRMLeadsPage currentUser={currentUser} />} />
                          <Route path="crm-leads/:id" element={<LeadDetailPage currentUser={currentUser} />} />
                          <Route path="orders" element={<OrdersPage currentUser={currentUser} />} />
                          <Route path="transfers" element={<RepTransfersPage />} />
                          <Route path="promo-codes" element={<PromoCodesPage currentUser={currentUser} />} />
                          <Route path="analytics" element={<AnalyticsPage currentUser={currentUser} />} />
                          <Route path="resources" element={<ResourcesPage />} />
                          <Route path="inventory" element={<InventoryPage currentUser={currentUser} />} />
                          <Route path="horses" element={<RepHorsesPage currentUser={currentUser} />} />
                          <Route path="horses/:horseId" element={<ClientHorseDetailPage currentUser={currentUser} basePath="/rep" />} />
                          <Route path="invoice/:orderId" element={<InvoicePaymentPage currentUser={currentUser} />} />
                          <Route path="*" element={<Navigate to="/rep/dashboard" replace />} />
                        </Routes>
                      </RepNavigation>
                    </RouteErrorBoundary>
                  </RepRouteWrapper>
                }
              />

              {/* Client Rep routes (backward compatibility) */}
              <Route
                path="/client_rep/*"
                element={
                  <RepRouteWrapper currentUser={currentUser} isLoading={isSessionLoading}>
                    <RouteErrorBoundary>
                      <RepNavigation currentUser={currentUser} onLogout={handleLogout}>
                        <Routes>
                          <Route path="dashboard" element={<RepDashboard currentUser={currentUser} />} />
                          <Route path="clients" element={<RepClientManagement currentUser={currentUser} />} />
                          <Route path="clients/:id" element={<RepClientDetailPage currentUser={currentUser} />} />
                          <Route path="crm-leads" element={<CRMLeadsPage currentUser={currentUser} />} />
                          <Route path="crm-leads/:id" element={<LeadDetailPage currentUser={currentUser} />} />
                          <Route path="orders" element={<OrdersPage currentUser={currentUser} />} />
                          <Route path="transfers" element={<RepTransfersPage />} />
                          <Route path="promo-codes" element={<PromoCodesPage currentUser={currentUser} />} />
                          <Route path="analytics" element={<AnalyticsPage currentUser={currentUser} />} />
                          <Route path="resources" element={<ResourcesPage />} />
                          <Route path="inventory" element={<InventoryPage currentUser={currentUser} />} />
                          <Route path="horses" element={<RepHorsesPage currentUser={currentUser} />} />
                          <Route path="horses/:horseId" element={<ClientHorseDetailPage currentUser={currentUser} basePath="/client_rep" />} />
                          <Route path="invoice/:orderId" element={<InvoicePaymentPage currentUser={currentUser} />} />
                          <Route path="*" element={<Navigate to="/rep/dashboard" replace />} />
                        </Routes>
                      </RepNavigation>
                    </RouteErrorBoundary>
                  </RepRouteWrapper>
                }
              />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>

        </BareThemeProvider>
      </PerspectiveProvider>
    </OrgProvider>
  );
}

export default App;
